/* eslint-disable max-lines-per-function */
import { ReactElement, useMemo } from 'react';

import {
   FinancialSettlementAdvancePayment,
   VisitMedicalService,
} from '@dreryk/egabinet-financial-settlement-frontend-api';
import { AppointmentAdvancePayment } from '@dreryk/egabinet-appointment-advance-payment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { LinkSize, StyledFlex, StyledMargin } from '@dreryk/react-components';

import { RefundAdvancePaymentShowModalEventInterceptor } from '~libs.desktop.shared.refund-advance-payment';
import { AdvancePaymentIssueActionLink } from '~libs.desktop.shared.appointment-advance-payment';

import { calendarTranslationKeys } from '../../../../../../translations';
import { areAdvancePaymentsAvailable } from '../../../../../../visitSettlement/shared/form/VisitSettlementForm.utils';
import { useMedicalServicesPrices } from '../../../../../../visitSettlement/shared/medicalServices/prices/MedicalServicesPrices.hooks';
import { isRefundingOnlinePaymentsAvailable } from '../../../../../../visitSettlement/shared/medicalServices/prices/MedicalServicesPrices.utils';

import { StyledLink } from './AppointmentAdvancePaymentLinks.styled';
import { isAppointmentAdvancePaymentAvailable } from './AppointmentAdvancePaymentInfoItem.utils';

const t = calendarTranslationKeys.appointmentContextDialog.tabs.appointmentDetailsTab.advancePayments;

type AppointmentAdvancePaymentLinkProps = {
   readonly appointmentId: number;
   readonly advancePayments: FinancialSettlementAdvancePayment[];
   readonly beginDate: Date;
   readonly endDate: Date;
   readonly medicalServices: VisitMedicalService[];
   readonly patientFirstName: string | null;
   readonly patientLastName: string | null;
   readonly onRefundSuccess: VoidFunction;
   readonly appointmentAdvancePayment: AppointmentAdvancePayment | null;
   readonly onlinePaymentsIntegrationExists: boolean;
};

export const AppointmentAdvancePaymentLinks = (props: AppointmentAdvancePaymentLinkProps): ReactElement | null => {
   const medicalServicesPrices = useMemo(
      () => props.medicalServices.map((medicalService) => medicalService.price.asNumber()),
      [props.medicalServices]
   );

   const { totalPriceAmount } = useMedicalServicesPrices({
      advancePayments: props.advancePayments,
      medicalServicesPrices: medicalServicesPrices,
   });

   const advancePaymentsAvailable = areAdvancePaymentsAvailable(props.advancePayments);
   const appointmentAdvancePaymentAvailable = isAppointmentAdvancePaymentAvailable(
      props.appointmentAdvancePayment,
      props.onlinePaymentsIntegrationExists
   );

   if ((!advancePaymentsAvailable && !appointmentAdvancePaymentAvailable) || !props.appointmentAdvancePayment) {
      return null;
   }

   const advancePayment = props.advancePayments?.[0]; // checked with BE - currently there will always be only one item... it's an array because it's prepared for future cash advance payments

   return (
      <StyledMargin left={24}>
         <StyledFlex gap={'12px'} direction={'column'}>
            {advancePaymentsAvailable && isRefundingOnlinePaymentsAvailable(advancePayment.transactionExternalId) ? (
               <RefundAdvancePaymentShowModalEventInterceptor
                  advancePayment={advancePayment}
                  beginDate={props.beginDate}
                  endDate={props.endDate}
                  patientFirstName={props.patientFirstName}
                  patientLastName={props.patientLastName}
                  totalPriceAmount={totalPriceAmount}
                  onSuccess={props.onRefundSuccess}
               >
                  <StyledLink size={LinkSize.NORMAL} testId={'refundAdvancePayment'} label={t.actions.label} />
               </RefundAdvancePaymentShowModalEventInterceptor>
            ) : null}
            {appointmentAdvancePaymentAvailable ? (
               <AdvancePaymentIssueActionLink
                  appointmentId={AppointmentId.of(props.appointmentId)}
                  requiredAmount={props.appointmentAdvancePayment.requiredAmount}
                  paidAmount={props.appointmentAdvancePayment.paidAmount}
                  remainingAmount={props.appointmentAdvancePayment.remainingAmount}
                  refundToBeCreatedAmount={props.appointmentAdvancePayment.refundToBeCreatedAmount}
                  pendingOnlinePayments={props.appointmentAdvancePayment.pendingOnlinePayments}
               />
            ) : null}
         </StyledFlex>
      </StyledMargin>
   );
};
